import { Col, Container, Row } from "react-bootstrap";
import { Link } from "react-router-dom";
import Hero from "../components/Hero";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { services, projects, testimonials } from "../utils/HomeContent";
import styles from "../styles/Home.module.css";

const consultSchema = z.object({
    name: z.string().trim().min(1, "Name is required").refine(v => !/\d/.test(v), "Name cannot contain numbers"),
    email: z.string().trim().min(1, "Email is required").email("Enter a valid email"),
    subject: z.string().trim().min(1, "Subject is required"),
    message: z.string().trim().min(1, "Message is required"),
});


export default function HomePage() {
    const [currentSlide, setCurrentSlide] = useState(0);

    const { register, handleSubmit, formState: { errors } } = useForm({
        resolver: zodResolver(consultSchema),
    });

    const onConsultSubmit = (data: unknown) => {
        // TODO: submit form
        console.log(data);
    };

    const nextSlide = () => {
        setCurrentSlide((prev) => (prev + 2 >= testimonials.length ? 0 : prev + 2));
    };

    const prevSlide = () => {
        setCurrentSlide((prev) => (prev - 2 < 0 ? testimonials.length - 2 : prev - 2));
    };

    return (
        <div className={styles.homePage}>
            <Hero />
            <section className={styles.servicesSection}>
                <Container>
                    <Row className="text-center mb-5">
                        <Col lg={12}>
                            <div className={styles.sectionBadge}>
                                <div className={styles.lines}>
                                    <img src="/icons/Line.svg" alt="line" />
                                </div>
                                <span>Our Services</span>
                            </div>
                            <h2 className={styles.sectionTitle}>Smarter Strategies, Bolder Investments, Stronger Communities</h2>
                        </Col>
                    </Row>
                    <Row className="g-4">
                        {services.map((service, index) => (
                            <Col lg={3} md={6} key={index}>
                                <div className={styles.serviceCard} style={{ backgroundColor: service.bgColor }}>
                                    <div className={styles.iconCircle}>
                                        <img src={service.icon} alt={service.title} />
                                    </div>
                                    <div className={styles.serviceContent}>
                                        <h3 className={styles.serviceTitle}>{service.title}</h3>
                                        <h4 className={styles.serviceSubtitle}>{service.subtitle}</h4>
                                        <p className={styles.serviceDescription}>{service.description}</p>
                                    </div>
                                </div>
                            </Col>
                        ))}
                    </Row>
                </Container>
            </section>
            <section className={styles.aboutSection}>
                <Container>
                    <Row>
                        <Col lg={6}>
                            <div className={styles.aboutImages}>
                                <div className={styles.image1}>
                                    <img src="/images/Home/KYC-1.svg" alt="Team Meeting" />
                                </div>
                                <div className={styles.image2}>
                                    <img src="/images/Home/KYC-2.jpg" alt="Office Work" />
                                </div>
                                <div className={styles.experienceBadge}>
                                    <div className={styles.number}>6</div>
                                    <div className={styles.text}>Years Experience</div>
                                </div>
                            </div>
                        </Col>
                        <Col lg={6}>
                            <div className={styles.aboutContent}>
                                <div className={styles.aboutSectionBadge}>
                                    <div className={styles.lines}>
                                        <img src="/icons/Line.svg" alt="line" />
                                    </div>
                                    <span>Know Our Company</span>
                                </div>
                                <h2 className={styles.sectionTitle}>Connecting the Dots</h2>
                                <p className={styles.sectionDescription}>
                                    At Symbioun, we believe sustainable growth hinges on factors beyond simple resource availability—it demands the symbiosis of businesses and communities with a shared collaborative vision. We specialise in connecting businesses with forward-thinking solutions and investment opportunities, with a strong commitment to community development for a brighter future. Our approach empowers organisations and individuals alike, creating vibrant ecosystems that elevate entire communities.
                                </p>
                                <div className={styles.statsContainer}>
                                    <div className={styles.statBox}>
                                        <div className={styles.statHeader}>Project success</div>
                                        <div className={styles.statContent}>
                                            <div className={styles.statNumber}> 95%</div>
                                            <div className={styles.rating}>
                                                <img src="/icons/star.svg" alt="Star" className={styles.star} />
                                                <img src="/icons/star.svg" alt="Star" className={styles.star} />
                                                <img src="/icons/star.svg" alt="Star" className={styles.star} />
                                                <img src="/icons/star.svg" alt="Star" className={styles.star} />
                                                <img src="/icons/star.svg" alt="Star" className={styles.star} />
                                            </div>
                                        </div>
                                    </div>
                                    <div className={styles.statBox}>
                                        <div className={styles.statHeader}>Customer Review</div>
                                        <div className={styles.statContent}>
                                            <div className={styles.statNumber}>4.7</div>
                                            <div className={styles.rating}>
                                                <img src="/icons/star.svg" alt="Star" className={styles.star} />
                                                <img src="/icons/star.svg" alt="Star" className={styles.star} />
                                                <img src="/icons/star.svg" alt="Star" className={styles.star} />
                                                <img src="/icons/star.svg" alt="Star" className={styles.star} />
                                                <img src="/icons/star.svg" alt="Star" className={styles.star} />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>
            <section className={styles.projectsSection}>
                <Container>
                    <Row className="text-center mb-5">
                        <Col lg={12}>
                            <div className={styles.sectionBadge}>
                                <div className={styles.lines}>
                                    <img src="/icons/Line.svg" alt="line" />
                                </div>
                                <span>Our Portfolio</span>
                            </div>
                            <h2 className={styles.sectionTitle}>Charting Our Impact</h2>
                            <p className={styles.sectionDescription}>
                                Explore the stories behind successful initiatives in consultation, investment, and community growth.
                            </p>
                        </Col>
                    </Row>
                    <Row className="g-4">
                        {projects.map((project, index) => (
                            <Col lg={4} md={6} key={index}>
                                <div className={styles.projectCard}>
                                    <div className={styles.projectImage} style={{ backgroundColor: project.bgColor }}>
                                        <img src={project.image} alt={project.title} />
                                    </div>
                                    <div className={styles.projectContent}>
                                        <div className={styles.projectDetails1}>
                                            <span className={styles.projectCategory}>{project.category}</span>
                                            <h3 className={styles.projectTitle}>{project.title}</h3>
                                            <p className={styles.projectDescription}>{project.description}</p>
                                        </div>
                                        <Link to={project.path} className={styles.projectDetails}>
                                            <img
                                                src="/images/Home/Project-background.svg"
                                                alt="background"
                                                className={styles.detailsBg}
                                            />
                                            <span className={styles.viewDetails}>View Details</span>
                                            <div className={styles.arrow}>
                                                <img src="/icons/doubleArrow.svg" alt="arrow" />
                                            </div>
                                        </Link>
                                    </div>
                                </div>
                            </Col>
                        ))}
                    </Row>
                    {/* <Row className="text-center mt-4">
                        <Col lg={12}>
                            <button className={styles.viewAllBtn}>
                                View All Projects
                                <img src="/icons/rightDoubleArrow.svg" alt="arrow" />
                            </button>
                        </Col>
                    </Row> */}
                </Container>
            </section>
            {/* <section className={styles.testimonialsSection}>
                <Container>
                    <Row className="text-center mb-5">
                        <Col lg={12}>
                            <div className={styles.sectionBadge}>
                                <div className={styles.lines}>
                                    <img src="/icons/Line.svg" alt="line" />
                                </div>
                                <span>Testimonials</span>
                            </div>
                            <h2 className={styles.sectionTitle}>What people say About Us</h2>
                        </Col>
                    </Row>
                    <Row className="g-4">
                        {testimonials.slice(currentSlide, currentSlide + 2).map((testimonial, index) => (
                            <Col lg={6} key={index}>
                                <div className={styles.testimonialCard}>
                                    <div className={styles.testimonialImage}>
                                        <img src={testimonial.image} alt={testimonial.name} />
                                    </div>
                                    <p className={styles.testimonialText}>{testimonial.text}</p>
                                    <h3 className={styles.testimonialName}>{testimonial.name}</h3>
                                    <p className={styles.testimonialPosition}>{testimonial.position}</p>
                                </div>
                            </Col>
                        ))}
                    </Row>
                    <Row className={styles.testimonialNav}>
                        <Col lg={12} className="text-center">
                            <div className={styles.navButtons}>
                                <button className={styles.navButton} onClick={prevSlide}>
                                    <img src="/icons/leftBlackArrow.svg" alt="Previous" />
                                </button>
                                <button className={styles.navButton} onClick={nextSlide}>
                                    <img src="/icons/rightBlackArrow.svg" alt="Next" />
                                </button>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section> */}

            <section className={styles.bgSection}>
                <div className={styles.bgImage}>
                    <div className={styles.gradientOverlay}></div>
                </div>
                <Container>
                    <Row>
                        <Col lg={6}>
                            <div className={styles.contactInfo}>
                                <h2 className={styles.contactTitle}>Curious about how we can help? </h2>
                                <p className={styles.contactDescription}>
                                    Let's connect! Reach out today and discover how we can empower your organisation and community together!
                                </p>
                                <div className={styles.contactDetails}>
                                    <div className={styles.contactItem}>
                                        <div className={styles.contactIcon}>
                                            <img src="/icons/Home/call.svg" alt="phone" />
                                        </div>
                                        <div className={styles.contactText}>
                                            <span className={styles.contactLabel}>Call Us Everyday</span>
                                            <span className={styles.contactValue}>+123 556 8824</span>
                                        </div>
                                    </div>
                                    <div className={styles.contactItem}>
                                        <div className={styles.contactIcon}>
                                            <img src="/icons/Home/email.svg" alt="email" />
                                        </div>
                                        <div className={styles.contactText}>
                                            <span className={styles.contactLabel}>Email Drop Us</span>
                                            <span className={styles.contactValue}>consulter@gmail.com</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Col>
                        <Col lg={6}>
                            <div className={styles.contactForm}>
                                <div className={styles.formBadge}>
                                    <div className={styles.lines}>
                                        <img src="/icons/Line.svg" alt="line" />
                                    </div>
                                    <span>GET IN TOUCH</span>
                                </div>
                                <h2 className={styles.formTitle}>Free Consultation</h2>
                                <form onSubmit={handleSubmit(onConsultSubmit)} noValidate>
                                    <div className={styles.formGroup}>
                                        <input {...register("name")} type="text" placeholder="Your Name" className={errors.name ? styles.inputError : ""} />
                                        {errors.name && <span className={styles.errorMsg}>{String(errors.name.message)}</span>}
                                    </div>
                                    <div className={styles.formGroup}>
                                        <input {...register("email")} type="email" placeholder="Your e-mail" className={errors.email ? styles.inputError : ""} />
                                        {errors.email && <span className={styles.errorMsg}>{String(errors.email.message)}</span>}
                                    </div>
                                    <div className={styles.formGroup}>
                                        <input {...register("subject")} type="text" placeholder="Subject" className={errors.subject ? styles.inputError : ""} />
                                        {errors.subject && <span className={styles.errorMsg}>{String(errors.subject.message)}</span>}
                                    </div>
                                    <div className={styles.formGroup}>
                                        <textarea {...register("message")} placeholder="Your Message" className={errors.message ? styles.inputError : ""}></textarea>
                                        {errors.message && <span className={styles.errorMsg}>{String(errors.message.message)}</span>}
                                    </div>
                                    <button type="submit" className={styles.submitBtn}>
                                        Free Consultant
                                        <img src="/icons/rightArrow.svg" alt="arrow" />
                                    </button>
                                </form>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>
            <section className={styles.partnersSection}>
                <Container>
                    <Row className="text-center mb-5">
                        <Col lg={12}>
                            <div className={styles.sectionBadge}>
                                <div className={styles.lines}>
                                    <img src="/icons/Line.svg" alt="line" />
                                </div>
                                <span>WHO HELP US</span>
                            </div>
                            <h2 className={styles.sectionTitle}>Our Partners & Donors</h2>
                        </Col>
                    </Row>
                    <Row>
                        <Col lg={12}>
                            <div className={styles.partnersContainer}>
                                <div className={styles.partnerImage}>
                                    <img src="/images/Home/Partner-1.jpg" alt="Partner 1" />
                                </div>
                                <div className={styles.partnerImage}>
                                    <img src="/images/Home/Partner-2.jpg" alt="Partner 2" />
                                </div>
                                <div className={styles.partnerImage}>
                                    <img src="/images/Home/Partner-3.jpg" alt="Partner 3" />
                                </div>
                                <div className={styles.partnerImage}>
                                    <img src="/images/Home/Partner-4.jpg" alt="Partner 4" />
                                </div>
                                <div className={styles.partnerImage}>
                                    <img src="/images/Home/Partner-5.jpg" alt="Partner 5" />
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>
        </div>
    );
}
